import { Component } from '@theme/component';

const RECENT_KEY = 'nuzzle-sample-recent-v1';
const MAX_RECENT = 7;

/** Accept only this storefront's sample URLs and simple display fields. */
function safeRecentItem(item) {
  if (!item || typeof item.title !== 'string' || typeof item.url !== 'string') return null;
  try {
    const url = new URL(item.url, location.origin);
    const image = new URL(item.image, location.origin);
    if (url.origin !== location.origin || !/^nr-sample-\d{2}$/.test(url.searchParams.get('view') || '') || !['https:', 'http:'].includes(image.protocol)) return null;
    return { url: `${url.pathname}${url.search}`, title: item.title.slice(0, 250), image: image.href, price: Number.isFinite(Number(item.price)) ? Number(item.price) : null, currency: /^[A-Z]{3}$/.test(item.currency || '') ? item.currency : null };
  } catch { return null; }
}

function recentItems() {
  try {
    const items = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(items) ? items.map(safeRecentItem).filter(Boolean).slice(0, MAX_RECENT) : [];
  } catch { return []; }
}

/** Local demonstration options; this component never submits a product or uploads a file. */
class RetailDetail extends Component {
  #controller;
  #objectUrl;
  #photoIndex = 0;
  #zoomTrigger;
  #previousOverflow;
  #touchStart;
  #swiped = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    const photos = [...this.querySelectorAll('[data-detail-photo]')];
    for (const [index, button] of photos.entries()) {
      button.addEventListener('click', () => this.#showPhoto(index), options);
      button.addEventListener('keydown', (event) => {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const forward = event.key === 'ArrowDown' || (event.key === 'ArrowRight' && document.documentElement.dir !== 'rtl') || (event.key === 'ArrowLeft' && document.documentElement.dir === 'rtl');
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? photos.length - 1 : (index + (forward ? 1 : -1) + photos.length) % photos.length;
        this.#showPhoto(next);
        photos[next]?.focus();
      }, options);
    }
    const main = this.querySelector('[data-open-zoom]');
    const dialog = this.querySelector('[data-detail-dialog]');
    const changePhotoByKey = (event) => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const forward = (event.key === 'ArrowRight') !== (document.documentElement.dir === 'rtl');
      this.#showPhoto(event.key === 'Home' ? 0 : event.key === 'End' ? photos.length - 1 : this.#photoIndex + (forward ? 1 : -1));
    };
    main?.addEventListener('keydown', changePhotoByKey, options);
    main?.addEventListener('click', (event) => {
      if (this.#swiped) { event.preventDefault(); this.#swiped = false; return; }
      if (!dialog?.showModal) return;
      event.preventDefault();
      this.#zoomTrigger = event.currentTarget;
      this.#showPhoto(this.#photoIndex);
      this.#previousOverflow = document.documentElement.style.overflow;
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.querySelector('[data-close-zoom]')?.focus();
    }, options);
    main?.addEventListener('touchstart', (event) => { this.#swiped = false; this.#touchStart = event.touches[0]?.clientX; }, { ...options, passive: true });
    main?.addEventListener('touchend', (event) => {
      if (!Number.isFinite(this.#touchStart)) return;
      const distance = (event.changedTouches[0]?.clientX ?? this.#touchStart) - this.#touchStart;
      this.#touchStart = undefined;
      if (Math.abs(distance) > 60) { this.#swiped = true; this.#showPhoto(this.#photoIndex + (distance < 0 ? 1 : -1)); }
    }, { ...options, passive: true });
    this.querySelector('[data-close-zoom]')?.addEventListener('click', () => dialog?.close(), options);
    this.querySelector('[data-zoom-previous]')?.addEventListener('click', () => this.#showPhoto(this.#photoIndex - 1), options);
    this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.#showPhoto(this.#photoIndex + 1), options);
    dialog?.addEventListener('keydown', changePhotoByKey, options);
    dialog?.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    }, options);
    dialog?.addEventListener('close', () => {
      document.documentElement.style.overflow = this.#previousOverflow || '';
      this.#zoomTrigger?.focus({ preventScroll: true });
    }, options);
    this.addEventListener('input', () => this.#updateSelections(), options);
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.matches('[data-detail-upload]')) this.#previewPhoto(event.target);
      this.#updateSelections();
    }, options);
    this.querySelector('[data-share-detail]')?.removeAttribute('hidden');
    this.querySelector('[data-share-detail]')?.addEventListener('click', () => this.#share(), options);
    this.querySelector('[data-clear-recent]')?.addEventListener('click', (event) => {
      if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
      try { localStorage.removeItem(RECENT_KEY); }
      catch { this.#recentStatus(this.dataset.recentError); return; }
      this.querySelector('[data-recent-grid]')?.replaceChildren();
      this.#recentStatus(this.dataset.recentCleared);
      event.currentTarget.setAttribute('aria-disabled', 'true');
    }, options);
    this.querySelector('[data-recent-grid]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-remove-recent]');
      if (!button) return;
      const next = recentItems().filter((item) => item.url !== button.dataset.removeRecent);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); }
      catch { this.#recentStatus(this.dataset.recentError); return; }
      const card = button.closest('.nr-card');
      const focusTarget = card.nextElementSibling?.querySelector('[data-remove-recent]') || card.previousElementSibling?.querySelector('[data-remove-recent]') || this.querySelector('[data-clear-recent]');
      card.remove();
      focusTarget?.focus({ preventScroll: true });
      this.#recentStatus(this.querySelector('[data-recent-grid]').children.length ? this.dataset.recentRemoved : this.dataset.recentEmpty);
    }, options);
    this.#showPhoto(this.#photoIndex);
    this.#updateSelections();
    this.#remember();
  }

  disconnectedCallback() {
    const dialog = this.querySelector('[data-detail-dialog]');
    if (dialog?.open) dialog.close();
    this.#controller?.abort();
    if (this.#objectUrl) URL.revokeObjectURL(this.#objectUrl);
    super.disconnectedCallback();
  }

  #showPhoto(index) {
    const photos = [...this.querySelectorAll('[data-detail-photo]')];
    if (!photos.length) return;
    this.#photoIndex = (index + photos.length) % photos.length;
    const src = photos[this.#photoIndex].dataset.detailPhoto;
    const description = `${this.dataset.title} — ${this.dataset.photoLabel} ${this.#photoIndex + 1}`;
    for (const image of this.querySelectorAll('[data-detail-main], [data-detail-zoom-image]')) { image.src = src; image.alt = description; }
    this.querySelector('[data-open-zoom]')?.setAttribute('href', src);
    for (const counter of this.querySelectorAll('[data-detail-counter], [data-zoom-counter]')) counter.textContent = `${this.#photoIndex + 1} / ${photos.length}`;
    for (const [photoIndex, button] of photos.entries()) {
      const active = photoIndex === this.#photoIndex;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.tabIndex = active ? 0 : -1;
    }
  }

  #updateSelections() {
    const list = this.querySelector('[data-selection-summary]');
    if (!list) return;
    list.replaceChildren();
    for (const field of this.querySelectorAll('[data-detail-option]')) {
      const input = field.querySelector('[data-detail-input]');
      if (!input) continue;
      const counter = field.querySelector('[data-detail-count]');
      if (counter) counter.textContent = `${input.value.length} / ${input.maxLength}`;
      const value = input.type === 'file' ? (input.files?.length ? this.dataset.photoChosen : '') : input.value.trim();
      if (!value) continue;
      const label = document.createElement('dt');
      label.textContent = field.dataset.label;
      const description = document.createElement('dd');
      description.textContent = value;
      list.append(label, description);
    }
    this.querySelector('[data-selection-empty]').hidden = list.children.length > 0;
  }

  #previewPhoto(input) {
    const image = this.querySelector('[data-detail-upload-image]');
    if (!image) return;
    if (this.#objectUrl) URL.revokeObjectURL(this.#objectUrl);
    this.#objectUrl = undefined;
    image.hidden = true;
    image.removeAttribute('src');
    const file = input.files?.[0];
    if (file?.type.startsWith('image/')) {
      this.#objectUrl = URL.createObjectURL(file);
      image.src = this.#objectUrl;
      image.hidden = false;
    }
  }

  #recentStatus(message) { const note = this.querySelector('[data-recent-note]'); if (note) note.textContent = message || ''; }

  #status(message) { const status = this.querySelector('[data-detail-status]'); if (status) status.textContent = message || ''; }

  async #share() {
    const url = new URL(this.dataset.url, location.origin).href;
    if (navigator.share) {
      try { await navigator.share({ title: this.dataset.title, url }); return; }
      catch (error) { if (error.name === 'AbortError') return; }
    }
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      this.#status(this.dataset.copySuccess);
    } catch {
      const fallback = this.querySelector('[data-share-fallback]');
      fallback.value = url;
      fallback.hidden = false;
      fallback.focus();
      fallback.select();
      this.#status(this.dataset.copyFailed);
    }
  }

  #remember() {
    const current = safeRecentItem({ url: this.dataset.url, title: this.dataset.title, image: this.dataset.image, price: this.dataset.price, currency: this.dataset.currency });
    if (!current) return;
    const items = recentItems().filter((item) => item.url !== current.url);
    const grid = this.querySelector('[data-recent-grid]');
    grid?.replaceChildren();
    this.querySelector('[data-detail-recent]')?.setAttribute('hidden', '');
    for (const item of items.slice(0, 6)) {
      const card = document.createElement('article'); card.className = 'nr-card';
      const media = document.createElement('div'); media.className = 'nr-card__media';
      const imageLink = document.createElement('a'); imageLink.href = item.url;
      const image = document.createElement('img'); image.src = item.image; image.alt = item.title; image.width = 800; image.height = 800; image.loading = 'lazy';
      imageLink.append(image); media.append(imageLink);
      const title = document.createElement('a'); title.className = 'nr-card__title'; title.href = item.url; title.textContent = item.title;
      card.append(media, title);
      if (item.price !== null && item.currency) {
        const price = document.createElement('div'); price.className = 'nr-card__price';
        try { price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: item.currency }).format(item.price / 100); card.append(price); } catch { /* No stale/mislabelled price. */ }
      }
      const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'nr-recent__remove';
      remove.dataset.removeRecent = item.url; remove.textContent = this.dataset.recentRemove;
      remove.setAttribute('aria-label', `${this.dataset.recentRemove}: ${item.title}`); card.append(remove);
      grid?.append(card);
    }
    if (items.length) { this.querySelector('[data-detail-recent]')?.removeAttribute('hidden'); this.querySelector('[data-clear-recent]')?.removeAttribute('aria-disabled'); }
    try { localStorage.setItem(RECENT_KEY, JSON.stringify([current, ...items].slice(0, MAX_RECENT))); } catch { /* The page works without browser storage. */ }
  }
}
if (!customElements.get('nr-detail')) customElements.define('nr-detail', RetailDetail);
